import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import './HemoDialysisFlowSheet.css';
import { useLocation } from 'react-router-dom';
import { makeApiCall } from '../Therapy/AssessmentCall';
import { DatePicker } from 'rsuite';
import { useAlert } from 'react-alert';
import TerminateDialog from './TerminateDialog.js'
import { GenerateAndUploadPDF } from './GenerateAndUploadPDF.js'

const FormInput = ({
    label,
    name,
    value,
    onChange,
    type = "text",
    placeholder,
    disabled = false,
    readOnly = false,
    rows,
    options,
    step,
    className = "",
    disabledHours,
    disabledMinutes,
    onClear,
    required = false,
    error = null,
    maxLength // Add this prop
}) => {
    const hasError = !!error;
    const inputProps = {
        name,
        value,
        onChange,
        placeholder,
        disabled,
        readOnly,
        className: `input-field ${className} ${required ? 'required' : ''} ${hasError ? 'error-field' : ''}`,
        ...(step && { step }),
        ...(type === 'textarea' && { rows: rows || 2 }),
        ...(maxLength && { maxLength })
    };

    // Add style prop for error state
    const inputStyle = hasError ? { border: '1px solid #ff4444' } : {};

    const renderInput = () => {
        switch (type) {
            case 'select':
                return (
                    <select {...inputProps} style={inputStyle}>
                        <option value="">Select</option>
                        {options?.map(opt => (
                            <option
                                key={opt.value}
                                value={opt.value}
                            >
                                {opt.label}
                            </option>
                        ))}
                    </select>
                );
            case 'textarea':
                return <textarea {...inputProps} style={inputStyle} />;
            case 'date':
                return (
                    <DatePicker
                        format="dd-MM-yyyy"
                        value={value ? new Date(value) : null}
                        onChange={(val) => {
                            const formatted = val ? val.toISOString().split('T')[0] : '';
                            onChange({ target: { name, value: formatted } });
                        }}
                        onClean={() => onChange({ target: { name, value: '' } })}
                        placeholder="Select date"
                        placement="topStart"
                        appearance="default"
                        style={{
                            width: '100%',
                            ...inputStyle
                        }}
                        disabled={disabled}
                    />
                );
            case 'time-picker':
                return (
                    <DatePicker
                        format="HH:mm:ss"
                        value={value ? new Date(`1970-01-01T${value}`) : null}
                        onChange={(val) => {
                            const formatted = val ? val.toTimeString().slice(0, 8) : '';
                            onChange({ target: { name, value: formatted } });
                        }}
                        onClean={() => onChange({ target: { name, value: '' } })}
                        hideHours={() => false}
                        hideMinutes={() => false}
                        placeholder="HH:MM:SS"
                        placement="topStart"
                        appearance="default"
                        showMeridian={false}
                        style={{
                            width: '100%',
                            ...inputStyle
                        }}
                        disabled={disabled}
                    />
                );
            default:
                return <input type={type} {...inputProps} style={inputStyle} />;
        }
    };

    return (
        <div className="input-group-physio">
            {hasError ? (
                <label style={{ color: '#ff4444' }}>{error}</label>
            ) : (
                <label>{label} {required && <span style={{ color: 'red' }}>*</span>}</label>
            )}
            {renderInput()}
        </div>
    );
};

const TableInput = ({
    value,
    onChange,
    type = "text",
    disabled = false,
    readOnly = false,
    step,
    disabledHours,
    disabledMinutes,
    onClear,
    required = false,
    error = false,
    maxLength // Add this prop
}) => {
    const inputStyle = error ? { border: '1px solid #ff4444' } : {};

    if (type === 'time-picker') {
        // time-picker logic remains the same...
        return (
            <DatePicker
                format="HH:mm:ss"
                value={value ? new Date(`1970-01-01T${value}`) : null}
                onChange={readOnly ? undefined : (val) => {
                    const formatted = val ? val.toTimeString().slice(0, 8) : '';
                    onChange({ target: { value: formatted } });
                }}
                onClean={readOnly ? undefined : () => onClear?.()}
                hideHours={() => false}
                hideMinutes={() => false}
                placeholder="HH:MM:SS"
                placement="topStart"
                appearance="default"
                showMeridian={false}
                style={{
                    width: '100%',
                    ...inputStyle
                }}
                disabled={disabled || readOnly}
            />
        );
    }

    return (
        <input
            type={type}
            value={value}
            onChange={readOnly ? undefined : onChange}
            className={`table-input ${readOnly ? 'readonly' : ''} ${required ? 'required' : ''} ${error ? 'error-field' : ''}`}
            disabled={disabled}
            readOnly={readOnly}
            {...(step && { step })}
            {...(maxLength && { maxLength })} // Add maxLength conditionally
            style={inputStyle}
        />
    );
};

const FormSection = ({ title, subtitle, children, className = "" }) => (
    <div className={`section ${className}`}>
        <div className="section-header">
            <h3>{title}</h3>
            {subtitle && <label>{subtitle}</label>}
        </div>
        {children}
    </div>
);

const HemodialysisFlowSheet = ({ data, onComplete, onClose }) => {
    const [doctors, setDoctors] = useState([]);
    const alert = useAlert()
    const elementRef = useRef()
    const [loading, setLoading] = useState(false)
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);
    const formattedDateTime = new Date().toISOString().slice(0, 19);
    const [apiDataLoaded, setApiDataLoaded] = useState(false)
    const [editedFields, setEditedFields] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [currentEditingRow, setCurrentEditingRow] = useState(null);
    const patientFields = [
        { name: 'name', label: 'Name', placeholder: 'Patient name...', disabled: true },
        { name: 'age', label: 'Age', type: 'number', placeholder: '', disabled: true },
        {
            name: 'gender',
            label: 'Gender',
            type: 'select',
            options: [
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' }
            ],
            disabled: true
        },
        { name: 'patient_id', label: 'ID/MrdNo', placeholder: 'Patient ID...', disabled: true }
    ];

    useEffect(() => {
        console.log(currentEditingRow)
    }, [currentEditingRow])

    const serviceFields = [
        { name: 'service', label: 'Service', placeholder: 'Service...', required: true, maxLength: 100 },
        { name: 'bed_no', label: 'Bed No', placeholder: 'Bed No...', required: true, maxLength: 10 },
        {
            name: 'admitting_doctor',
            type: 'select',
            label: 'Admitting Doctor',
            placeholder: 'Admitting Doctor...',
            options: doctors,
            required: true
        },
        { name: 'dialysis_no', label: 'No. of Dialysis', type: 'number', placeholder: 'No. of Dialysis...', required: true },
    ];

    const dialysisInfoFields = [
        { name: 'station', label: 'Station', placeholder: 'Station...', required: true, maxLength: 20 },
        { name: 'dialysis_appointment', label: 'Dialysis ID', placeholder: 'Dialysis ID...', disabled: true },
        { name: 'access', label: 'Access', placeholder: 'Access...', required: true, maxLength: 50 },
        { name: 'shift', label: 'Shift', placeholder: 'Shift...', required: true, maxLength: 20 },
    ];

    const orderFields1 = [
        { name: 'dialyzer', label: 'Dialyzer', placeholder: 'Dialyzer...', required: true, maxLength: 50 },
        { name: 'duration_specified', label: 'Duration', placeholder: 'hh:mm:ss', required: true, maxLength: 8 },
        { name: 'target_weight', label: 'Target Weight', type: 'number', placeholder: 'Target Weight...', required: true },
        { name: 'blood_flow', label: 'Blood Flow', type: 'number', placeholder: 'Blood Flow...', required: true }
    ];

    const orderFields2 = [
        { name: 'heparin', label: 'Heparin', placeholder: 'Heparin...', required: true, maxLength: 100 },
        { name: 'dialysate_na', label: 'Dialysate Na', placeholder: 'Dialysate Na...', required: true, maxLength: 50 },
        { name: 'uf_goal', label: 'UF Goal', type: 'number', placeholder: 'UF Goal...', required: true, maxLength: 50 },
        { name: 'other_orders', label: 'Other Orders', type: 'textarea', placeholder: 'Other orders...', required: true }
    ];

    const orderFields3 = [
        {
            name: 'physician',
            type: 'select',
            label: 'Physician',
            placeholder: 'Physician',
            options: doctors,
            required: true
        },
        {
            name: 'physician_approved',
            type: 'select',
            label: 'Physician Approved',
            placeholder: 'Select...',
            options: [{ value: 'approved', label: "Approved" }, { value: 'rejected', label: 'Rejected' }],
            required: true,
            maxLength: 200
        },
        { name: 'weight_gain', label: 'Wt Gain', type: 'number', placeholder: 'Weight gain...', required: true },
        { name: 'dialyzer_reuse', label: 'Dialyzer Reuse', type: 'number', placeholder: 'Dialyzer reuse...', required: true },
        { name: 'tubing_reuse_no', label: 'Tubing Reuse No', type: 'number', placeholder: 'Tubing reuse number...', required: true }
    ];

    const footerFields1 = [
        { name: 'nurse_remarks', label: "Nurse's Remarks", type: 'textarea', placeholder: "Nurse's remarks..." },
        { name: 'next_dialysis_due', label: 'Next Dialysis due', type: 'date' },
    ];
    const footerFields2 = [
        { name: 'dialysis_started_by_name', label: "Dialysis started by", },

    ];

    const convertToNumber = (value, fieldName) => {
        const numberFields = [
            'age', 'dialysis_no', 'target_weight', 'blood_flow', 'uf_goal', 'weight_gain', 'dialyzer_reuse',
            'tubing_reuse_no', 'weight', 'pulse_rate', 'respiration', 'temperature', 'patient_pulse', 'dialysate_flow'
        ];

        if (numberFields.includes(fieldName) && value !== '') {
            const num = Number(value);
            return isNaN(num) ? 0 : num;
        }
        return value;
    };

    const handleTime = (name, timeStr) => {
        setInfo(prev => ({ ...prev, [name]: timeStr }));
    };

    const handleTimeChangeForRow = (index, timeStr) => {
        setDialysisRun(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], time: timeStr };
            return updated;
        });
    };

    const initializeInfo = useCallback(() => {
        const defaultInfo = {};
        [...patientFields, ...serviceFields, ...dialysisInfoFields,
        ...orderFields1, ...orderFields2, ...orderFields3, ...footerFields1,]
            .forEach(field => {
                let value = data?.[field.name];

                if (field.name === 'dialyzer_reuse' && (value === undefined || value === null)) {
                    value = '';
                }

                defaultInfo[field.name] = convertToNumber(value, field.name);
            });
        return defaultInfo;
    }, [data]);

    const [info, setInfo] = useState(initializeInfo);

    const initializePrePostDialysis = useCallback((data) => ({
        pre: {
            weight: convertToNumber(data?.pre_dialysis?.weight || '', 'weight'),
            supine_bp: data?.pre_dialysis?.supine_bp || '',
            standing_bp: data?.pre_dialysis?.standing_bp || '',
            pulse_rate: convertToNumber(data?.pre_dialysis?.pulse_rate || '', 'pulse_rate'),
            respiration: convertToNumber(data?.pre_dialysis?.respiration || '', 'respiration'),
            temperature: convertToNumber(data?.pre_dialysis?.temperature || '', 'temperature'),
            symptoms_remarks: data?.pre_dialysis?.symptoms_remarks || ''
        },
        post: {
            weight: convertToNumber(data?.post_dialysis?.weight || '', 'weight'),
            supine_bp: data?.post_dialysis?.supine_bp || '',
            standing_bp: data?.post_dialysis?.standing_bp || '',
            pulse_rate: convertToNumber(data?.post_dialysis?.pulse_rate || '', 'pulse_rate'),
            respiration: convertToNumber(data?.post_dialysis?.respiration || '', 'respiration'),
            temperature: convertToNumber(data?.post_dialysis?.temperature || '', 'temperature'),
            symptoms_remarks: data?.post_dialysis?.symptoms_remarks || ''
        }
    }), []);

    const initializeDialysisRun = useCallback((data) => {
        const initialInferences = data?.inferences || [];
        const initialRows = [];

        if (initialInferences.length > 0) {
            initialInferences.forEach((inference, index) => {
                initialRows.push({
                    id: inference.id || null,
                    time: inference.time || '',
                    machine_blood_flow: convertToNumber(inference.machine_blood_flow || '', 'machine_blood_flow'),
                    dialysate_flow: convertToNumber(inference.dialysate_flow || '', 'dialysate_flow'),
                    tmp: convertToNumber(inference.tmp || '', 'tmp'),
                    various_pressure: inference.various_pressure || '',
                    heparin: inference.heparin || '',
                    patient_pulse: convertToNumber(inference.patient_pulse || '', 'patient_pulse'),
                    patient_bp: inference.patient_bp || '',
                    symptoms_events: inference.symptoms_events || '',
                    drugs_fluids: inference.drugs_fluids || ''
                });
            });
        }

        while (initialRows.length < 6) {
            initialRows.push({
                id: null,
                time: '',
                machine_blood_flow: 0,
                dialysate_flow: 0,
                tmp: 0,
                various_pressure: '',
                heparin: '',
                patient_pulse: 0,
                patient_bp: '',
                symptoms_events: '',
                drugs_fluids: ''
            });
        }

        return initialRows;
    }, []);
    const [prePostDialysis, setPrePostDialysis] = useState(() => initializePrePostDialysis(data));
    const [dialysisRun, setDialysisRun] = useState(() => initializeDialysisRun(data));

    useEffect(() => {
        if (data) {
            setPrePostDialysis(prev => {
                const newState = initializePrePostDialysis(data);

                if (Object.keys(editedFields).length > 0) {
                    const preFields = ['weight', 'supine_bp', 'standing_bp', 'pulse_rate', 'respiration', 'temperature', 'symptoms_remarks'];
                    const postFields = ['weight', 'supine_bp', 'standing_bp', 'pulse_rate', 'respiration', 'temperature', 'symptoms_remarks'];

                    preFields.forEach(field => {
                        if (editedFields[`pre_${field}`] !== undefined) {
                            newState.pre[field] = editedFields[`pre_${field}`];
                        }
                    });

                    postFields.forEach(field => {
                        if (editedFields[`post_${field}`] !== undefined) {
                            newState.post[field] = editedFields[`post_${field}`];
                        }
                    });
                }

                return newState;
            });
        }
    }, [data, initializePrePostDialysis, editedFields]);

    useEffect(() => {
        if (data) {
            setDialysisRun(initializeDialysisRun(data));
        }
    }, [data, initializeDialysisRun]);

    useEffect(() => {
        if (data && Object.keys(editedFields).length > 0) {
            // Restore edited main fields
            const editableMainFields = ['nurse_remarks', 'next_dialysis_due'];

            setInfo(prev => {
                const restoredInfo = { ...prev };

                editableMainFields.forEach(field => {
                    if (editedFields[field] !== undefined) {
                        restoredInfo[field] = editedFields[field];
                    }
                });

                return restoredInfo;
            });
        }
    }, [data, editedFields]);

    const validateDurationFormat = (duration) => {
        if (!duration) return false;
        const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;
        return timeRegex.test(duration);
    };

    const validateMandatoryFields = useCallback(() => {
        const errors = {};
        const errorMessages = [];

        // All mandatory fields from the form sections
        const mandatoryFields = [
            ...serviceFields.filter(f => f.required),
            ...dialysisInfoFields.filter(f => f.required),
            ...orderFields1.filter(f => f.required),
            ...orderFields2.filter(f => f.required),
            ...orderFields3.filter(f => f.required)
        ];

        mandatoryFields.forEach(field => {
            const value = info[field.name];
            const isEmpty = value === '' || value === null || value === undefined ||
                (typeof value === 'number' && value === 0)

            if (isEmpty) {
                errors[field.name] = `${field.label} is required!`;
                errorMessages.push(`${field.label} is required!`);
            }

            if (field.name === 'duration_specified' && !isEmpty) {
                if (!validateDurationFormat(value)) {
                    errors[field.name] = 'Must be in HH:MM:SS format';
                    errorMessages.push('Must be in HH:MM:SS format');
                }
            }
        });
        // Check pre-dialysis data (all fields are mandatory)
        const preDialysisFields = [
            { key: 'weight', label: 'Pre-Dialysis Weight' },
            { key: 'supine_bp', label: 'Pre-Dialysis Supine BP' },
            { key: 'standing_bp', label: 'Pre-Dialysis Standing BP' },
            { key: 'pulse_rate', label: 'Pre-Dialysis Pulse Rate' },
            { key: 'respiration', label: 'Pre-Dialysis Respiration' },
            { key: 'temperature', label: 'Pre-Dialysis Temperature' },
            { key: 'symptoms_remarks', label: 'Pre-Dialysis Symptoms/Remarks' }
        ];

        preDialysisFields.forEach(field => {
            const value = prePostDialysis.pre[field.key];
            const isEmpty = value === '' || value === null || value === undefined ||
                (typeof value === 'number' && value === 0);

            if (isEmpty) {
                errors[`pre_${field.key}`] = `${field.label} is required`;
                errorMessages.push(`${field.label} is required`);
            }
        });

        return { errors, errorMessages };
    }, [info, prePostDialysis.pre]);

    // New function to validate inference row data
    // First, update the validateInferenceRow function to be more specific about errors
    const validateInferenceRow = (row, rowIndex) => {
        const requiredFields = [
            'time', 'machine_blood_flow', 'dialysate_flow', 'tmp',
            'various_pressure', 'heparin', 'patient_pulse', 'patient_bp',
            'symptoms_events', 'drugs_fluids'
        ];

        // Check if any field is filled
        const hasData = requiredFields.some(field => {
            const value = row[field];
            return value !== '' && value !== 0 && value !== null && value !== undefined;
        });

        // If no fields are filled, it's valid (empty row)
        if (!hasData) {
            return { isValid: true, isEmpty: true };
        }

        // Check which fields are missing
        const missingFields = requiredFields.filter(field => {
            const value = row[field];
            return value === '' || value === 0 || value === null || value === undefined;
        });

        // If all fields are filled, it's valid
        if (missingFields.length === 0) {
            return { isValid: true, isEmpty: false };
        }

        return {
            isValid: false,
            isEmpty: false,
            missingFields,
            filledFields: requiredFields.filter(field => !missingFields.includes(field))
        };
    };

    const validateAllInferenceData = () => {
        const validationResults = [];
        let hasErrors = false;

        dialysisRun.forEach((row, index) => {
            const validation = validateInferenceRow(row, index);
            validationResults.push({ ...validation, rowIndex: index });

            if (!validation.isValid) {
                hasErrors = true;
            }
        });

        return { validationResults, hasErrors };
    };

    // Keep these functions as they are - no changes needed:
    const addDialysisRow = () => {
        setDialysisRun(prev => [...prev, {
            id: null,
            time: '',
            machine_blood_flow: 0,
            dialysate_flow: 0,
            tmp: 0,
            various_pressure: '',
            heparin: '',
            patient_pulse: 0,
            patient_bp: '',
            symptoms_events: '',
            drugs_fluids: ''
        }]);
    };

    const removeDialysisRow = (index) => {
        if (dialysisRun.length > 1) {
            setDialysisRun(prev => prev.filter((_, i) => i !== index));
        }
        setCurrentEditingRow(null)
    };

    const getDoctors = async () => {
        try {
            const response = await makeApiCall({
                method: 'get',
                endpoint: '/api/v1/service-provider/procedure-assignee/',
            });
            return response.data.details
        } catch (error) {
            console.error('Error fetching doctors:', error);
            return [];
        }
    };

    useEffect(() => {
        const fetchAPIData = async () => {
            if (apiDataLoaded) return;

            try {
                const [doctorsData] = await Promise.all([
                    getDoctors(),
                ]);

                const doctorsOptions = doctorsData?.map(doctor => ({
                    value: doctor.id,
                    label: doctor.full_name
                })) || [];

                setDoctors(doctorsOptions);
                setApiDataLoaded(true);

            } catch (error) {
                console.error('Failed to fetch doctors', error);
                setDoctors([]);
                setApiDataLoaded(true);
            }
        };

        fetchAPIData();
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        const convertedValue = convertToNumber(value, name);

        const editableFields = ['nurse_remarks', 'next_dialysis_due'];
        if (editableFields.includes(name)) {
            setEditedFields(prev => ({
                ...prev,
                [name]: convertedValue
            }));
        }
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        setInfo(prev => ({ ...prev, [name]: convertedValue }));
    }, [fieldErrors]);

    const handlePrePostChange = useCallback((type, field, value) => {
        const convertedValue = convertToNumber(value, field);

        if (type === 'post') {
            setEditedFields(prev => ({
                ...prev,
                [`post_${field}`]: convertedValue
            }));
        } else if (type === 'pre') {
            setEditedFields(prev => ({
                ...prev,
                [`pre_${field}`]: convertedValue
            }));
        }

        const errorKey = `${type}_${field}`;
        if (fieldErrors[errorKey]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }

        setPrePostDialysis(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: convertedValue }
        }));
    }, [fieldErrors]);

    const handleDialysisRunChange = useCallback((index, field, value) => {
        if (currentEditingRow !== null && currentEditingRow !== index && data.id) {
            alert.error('Please update or remove the edited row before editing another');
            return;
        }
        const convertedValue = convertToNumber(value, field);
        setDialysisRun(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: convertedValue };
            return updated;
        });
        setCurrentEditingRow(index);
    }, [currentEditingRow]);

    const handleUpdateInference = useCallback(async (index) => {
        const inference = dialysisRun[index];

        const validation = validateInferenceRow(inference, index);

        if (validation.isEmpty) {
            alert.error('Please fill in all fields for this record');
            return;
        }

        if (!validation.isValid) {
            alert.error(`Row ${index + 1}: Please fill in all fields.`);
            return;
        }

        const payload = {
            haemodialysis: data.id,
            time: inference.time,
            machine_blood_flow: Number(inference.machine_blood_flow || 0),
            dialysate_flow: Number(inference.dialysate_flow || 0),
            tmp: Number(inference.tmp || 0),
            various_pressure: inference.various_pressure,
            heparin: inference.heparin,
            patient_pulse: Number(inference.patient_pulse || 0),
            patient_bp: inference.patient_bp,
            symptoms_events: inference.symptoms_events,
            drugs_fluids: inference.drugs_fluids
        };

        try {
            const response = await makeApiCall({
                method: 'put',
                endpoint: `/api/v1/patient/haemodialysis/?haemodialysis=${data.id}`,
                data: payload,
            });
            if (response.success) {
                setCurrentEditingRow(null)
                alert.success('Inference updated successfully');
                onComplete('infUpdate')
                if (!index.id && response.data?.id) {
                    setDialysisRun(prev => {
                        const updated = [...prev];
                        updated[index] = { ...updated[index], id: response.data.id };
                        return updated;
                    });
                }
            } else {
                alert.error('Failed to update inference');
            }
        } catch (error) {
            console.error('Error updating inference:', error);
            alert.error('Error updating inference');
        }
    }, [dialysisRun, data.id, alert, onComplete, currentEditingRow]);

    const handleSubmit = useCallback(async () => {
        setLoading(true)
        const validation = validateMandatoryFields();

        if (validation.errorMessages.length > 0) {
            setLoading(false)
            setFieldErrors(validation.errors);
            alert.error('Please fill all the required fields');
            return;
        }
        const { validationResults, hasErrors } = validateAllInferenceData();

        if (hasErrors) {
            const errorMessages = validationResults
                .filter(result => !result.isValid)
                .map(result => `Row ${result.rowIndex + 1}: Please fill all fields or leave row completely empty.`)
                .join('\n');

            alert.error(`Please fix the following errors:\n${errorMessages}`);
            setLoading(false)
            return;
        }

        const validInferences = validationResults
            .filter(result => result.isValid && !result.isEmpty)
            .map(result => {
                const row = dialysisRun[result.rowIndex];
                return {
                    time: row.time,
                    machine_blood_flow: Number(row.machine_blood_flow || 0),
                    dialysate_flow: Number(row.dialysate_flow || 0),
                    tmp: Number(row.tmp || 0),
                    various_pressure: row.various_pressure,
                    heparin: row.heparin,
                    patient_pulse: Number(row.patient_pulse || 0),
                    patient_bp: row.patient_bp,
                    symptoms_events: row.symptoms_events,
                    drugs_fluids: row.drugs_fluids
                };
            });

        const postDialysisData = {
            weight: Number(prePostDialysis.post.weight || 0),
            supine_bp: prePostDialysis.post.supine_bp,
            standing_bp: prePostDialysis.post.standing_bp,
            pulse_rate: Number(prePostDialysis.post.pulse_rate || 0),
            respiration: Number(prePostDialysis.post.respiration || 0),
            temperature: Number(prePostDialysis.post.temperature || 0),
            symptoms_remarks: prePostDialysis.post.symptoms_remarks,
        };

        const hasPostDialysisData = Object.values(postDialysisData).some(value =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== 0
        );

        const payload = {
            service: info.service,
            dialysis_appointment: Number(info.dialysis_appointment || 0),
            dialysis_no: Number(info.dialysis_no || 0),
            dialysis_run_date: formattedDateTime,
            shift: info.shift,
            station: info.station,
            bed_no: info.bed_no,
            access: info.access,
            dialyzer: info.dialyzer,
            duration_specified: info.duration_specified,
            target_weight: Number(info.target_weight || 0),
            blood_flow: Number(info.blood_flow || 0),
            heparin: info.heparin,
            dialysate_na: info.dialysate_na,
            uf_goal: Number(info.uf_goal || 0),
            other_orders: info.other_orders,
            physician_approved: info.physician_approved,
            weight_gain: Number(info.weight_gain || 0),
            dialyzer_reuse: info.dialyzer_reuse,
            tubing_reuse_no: Number(info.tubing_reuse_no || 0),
            patient: info.patient_id,
            admitting_doctor: Number(info.admitting_doctor || 0),
            physician: Number(info.physician || 0),
            inferences: validInferences,
            pre_dialysis: {
                weight: Number(prePostDialysis.pre.weight || 0),
                supine_bp: prePostDialysis.pre.supine_bp,
                standing_bp: prePostDialysis.pre.standing_bp,
                pulse_rate: Number(prePostDialysis.pre.pulse_rate || 0),
                respiration: Number(prePostDialysis.pre.respiration || 0),
                temperature: Number(prePostDialysis.pre.temperature || 0),
                symptoms_remarks: prePostDialysis.pre.symptoms_remarks,
            },
            ...(hasPostDialysisData ? { post_dialysis: postDialysisData } : {}),
            nurse_remarks: info.nurse_remarks,
            next_dialysis_due: info.next_dialysis_due,
            dialysis_doc: 'doc'
        };

        try {
            const response = await makeApiCall({
                method: 'post',
                endpoint: '/api/v1/patient/haemodialysis/',
                data: payload,
            });
            if (response.success) {
                setCurrentEditingRow(null)
                alert.success('Updated successfully')
                setLoading(false)
                onComplete('update')
            } else {
                alert.error(response.error || 'Submission failed');
                setLoading(false)
            }
        } catch (error) {
            alert.error(error || 'Submission failed');
            setLoading(false)
        }

    }, [info, prePostDialysis, dialysisRun, onComplete, alert, validateMandatoryFields]);

    const handleTerminate = () => {
        const { validationResults, hasErrors } = validateAllInferenceData();

        if (hasErrors) {
            const errorMessages = validationResults
                .filter(result => !result.isValid)
                .map(result => `Row ${result.rowIndex + 1}: Please fill all fields or leave row completely empty.`)
                .join('\n');

            alert.error(`Please fix the following errors:\n${errorMessages}`);
            setLoading(false)
            return;
        } else {
            setShowTerminateDialog(true);
        }
    };


    const getPendingInferenceUpdates = () => {
        return dialysisRun
            .filter((row, index) => {
                const validation = validateInferenceRow(row, index);
                return validation.isValid && !validation.isEmpty && !row.id && index !== currentEditingRow;
            })
            .map((row, index) => ({
                index,
                data: {
                    time: row.time,
                    machine_blood_flow: Number(row.machine_blood_flow || 0),
                    dialysate_flow: Number(row.dialysate_flow || 0),
                    tmp: Number(row.tmp || 0),
                    various_pressure: row.various_pressure,
                    heparin: row.heparin,
                    patient_pulse: Number(row.patient_pulse || 0),
                    patient_bp: row.patient_bp,
                    symptoms_events: row.symptoms_events,
                    drugs_fluids: row.drugs_fluids
                }
            }));
    };

    const handleTerminateConfirm = async (terminationRemarks) => {
        setLoading(true)

        const hasPostData = prePostDialysis.post &&
            (prePostDialysis.post.weight ||
                prePostDialysis.post.supine_bp ||
                prePostDialysis.post.standing_bp ||
                prePostDialysis.post.pulse_rate ||
                prePostDialysis.post.respiration ||
                prePostDialysis.post.temperature ||
                prePostDialysis.post.symptoms_remarks);


        try {
            const pdfResult = await GenerateAndUploadPDF(elementRef, info.patient_id, data.id, 'dialysis');

            if (!pdfResult.success) {
                alert.error(pdfResult.error || 'Failed to generate PDF');
                return;
            }

            const payload = {
                ...(hasPostData && {
                    post_dialysis: {
                        weight: Number(prePostDialysis.post.weight || 0),
                        supine_bp: prePostDialysis.post.supine_bp,
                        standing_bp: prePostDialysis.post.standing_bp,
                        pulse_rate: Number(prePostDialysis.post.pulse_rate || 0),
                        respiration: Number(prePostDialysis.post.respiration || 0),
                        temperature: Number(prePostDialysis.post.temperature || 0),
                        symptoms_remarks: prePostDialysis.post.symptoms_remarks,
                    }
                }),
                terminate: true,
                terminated_remarks: terminationRemarks,
                dialysis_doc: pdfResult.document
            };

            if (info.nurse_remarks !== data.nurse_remarks) {
                payload.nurse_remarks = info.nurse_remarks;
            }

            if (info.next_dialysis_due !== data.next_dialysis_due) {
                payload.next_dialysis_due = info.next_dialysis_due;
            }

            const response = await makeApiCall({
                method: 'put',
                endpoint: `/api/v1/patient/haemodialysis/?haemodialysis=${data.id}`,
                data: payload,
            });

            if (response.success) {
                alert.success('Dialysis terminated successfully');
                onComplete('end')
                setShowTerminateDialog(false);
                setLoading(false)
            } else {
                alert.error('Termination failed');
                setShowTerminateDialog(false);
                setLoading(false)
            }
        } catch (error) {
            console.error('Error terminating dialysis:', error);
            alert.error('Error terminating dialysis');
            setShowTerminateDialog(false);
            setLoading(false)
        }
    };

    const clearEditedFields = () => {
        setEditedFields({});
    };

    const clearAllErrors = useCallback(() => {
        setFieldErrors({});
    }, []);

    const handleEnd = async () => {
        try {
            setLoading(true);
            if (currentEditingRow !== null) {
                alert.error('Please update or remove the edited dialysis run data before ending dialysis');
                setLoading(false);
                return;
            }

            const pendingUpdates = getPendingInferenceUpdates();

            if (pendingUpdates.length > 0) {
                for (const update of pendingUpdates) {
                    const payload = {
                        haemodialysis: data.id,
                        ...update.data
                    };

                    const response = await makeApiCall({
                        method: 'put',
                        endpoint: `/api/v1/patient/haemodialysis/?haemodialysis=${data.id}`,
                        data: payload,
                    });

                    if (!response.success) {
                        alert.error(`Failed to update row ${update.index + 1}`);
                        setLoading(false);
                        return;
                    }

                    setDialysisRun(prev => {
                        const updated = [...prev];
                        updated[update.index] = { ...updated[update.index], id: response.data.id };
                        return updated;
                    });
                }

                alert.success('All pending rows updated successfully');
            }
            const pdfResult = await GenerateAndUploadPDF(elementRef, info.patient_id, data.id, 'completed_dialysis');

            if (!pdfResult.success) {
                alert.error(pdfResult.error || 'Failed to generate PDF');
                setLoading(false)
                return;
            }

            const payload = {
                status: 'completed',
                post_dialysis: {
                    weight: Number(prePostDialysis.post.weight || 0),
                    supine_bp: prePostDialysis.post.supine_bp,
                    standing_bp: prePostDialysis.post.standing_bp,
                    pulse_rate: Number(prePostDialysis.post.pulse_rate || 0),
                    respiration: Number(prePostDialysis.post.respiration || 0),
                    temperature: Number(prePostDialysis.post.temperature || 0),
                    symptoms_remarks: prePostDialysis.post.symptoms_remarks,
                },
                dialysis_doc: pdfResult.document
            };
            if (info.nurse_remarks !== (data.nurse_remarks)) {
                payload.nurse_remarks = info.nurse_remarks;
            }
            if (info.next_dialysis_due !== (data.next_dialysis_due)) {
                payload.next_dialysis_due = info.next_dialysis_due;
            }
            const response = await makeApiCall({
                method: 'put',
                endpoint: `/api/v1/patient/haemodialysis/?haemodialysis=${data.id}`,
                data: payload,
            });

            if (response.success) {
                alert.success('Dialysis ended successfully');
                resetForm();
                clearEditedFields();
                onComplete('end');
                setLoading(false)
            } else {
                alert.error('End dialysis failed');
                setLoading(false)
            }
        } catch (error) {
            console.error('Error ending dialysis:', error);
            alert.error('Error ending dialysis');
            setLoading(false)
        }
    };

    const resetForm = useCallback(() => {
        const resetInfo = {};
        [...dialysisInfoFields, ...orderFields1, ...orderFields2,
        ...orderFields3, ...footerFields1]
            .forEach(field => {
                if (!['name', 'age', 'gender', 'patient_id', 'service', 'admitting_doctor', 'physician'].includes(field.name)) {
                    resetInfo[field.name] = field.type === 'number' ? 0 : '';
                }
            });

        setInfo(prev => ({ ...prev, ...resetInfo }));
        setPrePostDialysis({
            pre: { weight: 0, supine_bp: '', standing_bp: '', pulse_rate: 0, respiration: 0, temperature: 0, symptoms_remarks: '' },
            post: { weight: 0, supine_bp: '', standing_bp: '', pulse_rate: 0, respiration: 0, temperature: 0, symptoms_remarks: '' }
        });

        // Reset to minimum 6 empty rows instead of using timePoints
        setDialysisRun(Array(6).fill().map(() => ({
            id: null,
            time: '',
            machine_blood_flow: 0,
            dialysate_flow: 0,
            tmp: 0,
            various_pressure: '',
            heparin: '',
            patient_pulse: 0,
            patient_bp: '',
            symptoms_events: '',
            drugs_fluids: ''
        })));
        clearAllErrors()
    }, []);

    const isFieldDisabled = (fieldName) => {
        if (!data.id) return false;
        const editableFields = ['nurse_remarks', 'next_dialysis_due'];
        if (editableFields.includes(fieldName)) {
            return false;
        }
        return true;
    };

    const renderFieldGroup = (fields, rowClass = "no4") => (
        <div className={`input-row ${rowClass}`}>
            {fields.map(field => (
                <FormInput
                    key={field.name}
                    type={field.type || 'text'}
                    {...field}
                    value={info[field.name]}
                    onChange={handleChange}
                    disabled={field.disabled || isFieldDisabled(field.name) || field.readOnly}
                    required={field.required}
                    error={fieldErrors[field.name]}
                    maxLength={field.maxLength}
                />
            ))}
        </div>
    );

    const prePostFields = [
        { key: 'weight', label: 'Weight', type: 'number', step: '0.1', placeholder: 'kg', required: true },
        { key: 'supine_bp', label: 'Supine BP', placeholder: '120/80', required: true },
        { key: 'standing_bp', label: 'Standing BP', placeholder: '120/80', required: true },
        { key: 'pulse_rate', label: 'PR', type: 'number', placeholder: 'bpm', required: true },
        { key: 'respiration', label: 'Resp', type: 'number', placeholder: '/min', required: true },
        { key: 'temperature', label: 'Temp', type: 'number', step: '0.1', placeholder: '°F', required: true },
        { key: 'symptoms_remarks', label: 'Symptoms/Remarks', placeholder: 'Symptoms/Remarks', required: true }
    ];

    // Get current hour and minute for time restrictions
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // Fixed: Helper function to check if a row has any data
    const hasRowData = (row) => {
        return row.time ||
            row.machine_blood_flow ||
            row.dialysate_flow ||
            row.tmp ||
            row.various_pressure ||
            row.heparin ||
            row.patient_pulse ||
            row.patient_bp ||
            row.symptoms_events ||
            row.drugs_fluids;
    };

    return (
        <div className='assessment-section' >
            <div className="general-form-content" ref={elementRef} style={{ padding: '15px 0' }}>
                <div className="section-header">
                    <div className="centre-info">
                        <h2>HEMODIALYSIS FLOW SHEET</h2>
                    </div>
                </div>

                <FormSection title="Demographic data" className="patient-information">
                    {renderFieldGroup(patientFields)}
                    {renderFieldGroup(serviceFields)}
                    {renderFieldGroup(dialysisInfoFields)}
                </FormSection>

                <FormSection
                    title="Dialysis Orders"
                    subtitle="(To be copied by Nurse from Chart. Physician should sign before closing Dialysis)"
                    className="dialysis-orders"
                >
                    {renderFieldGroup(orderFields1)}
                    {renderFieldGroup(orderFields2)}
                    {renderFieldGroup(orderFields3)}
                </FormSection>

                <FormSection title="Pre/Post Dialysis Measurements" className="pre-post-dialysis">
                    <div className="assessment-table">
                        <table className="dialysis-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    {prePostFields.map(field => (
                                        <th key={field.key}>
                                            {field.label}                                                                                    </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {['pre', 'post'].map(type => (
                                    <tr key={type}>
                                        <td className="catgory-cell">
                                            <strong>{type === 'pre' ? 'Pre Dialysis' : 'Post Dialysis'}</strong>
                                            {type === 'pre' && <span style={{ color: 'red' }}>*</span>}
                                        </td>
                                        {prePostFields.map(field => (
                                            <td key={field.key} className="input-cell">
                                                <TableInput
                                                    type={field.type || 'text'}
                                                    value={prePostDialysis[type][field.key]}
                                                    onChange={(e) => handlePrePostChange(type, field.key, e.target.value)}
                                                    disabled={data.id && type === 'pre'}
                                                    step={field.step}
                                                    required={type === 'pre' && field.required}
                                                    error={type === 'pre' && !!fieldErrors[`pre_${field.key}`]}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </FormSection>

                <FormSection title="Dialysis Run Data (For Stable patients data be recorded every 30 minutes only)" className="dialysis-run">
                    <div className="assessment-table">
                        <table className="dialysis-table dialysis-run-table">
                            <thead>
                                <tr>
                                    <th rowSpan="2">Time</th>
                                    <th colSpan="5">Machine Data</th>
                                    <th colSpan="3">Patient Data</th>
                                    <th rowSpan="2">Drugs/Fluids</th>
                                    <th rowSpan="2">Action</th>
                                </tr>
                                <tr>
                                    <th>Blood Flow</th>
                                    <th>Dialysate Flow</th>
                                    <th>TMP</th>
                                    <th>Venous Pressure</th>
                                    <th>Heparin</th>
                                    <th>Pulse</th>
                                    <th>BP</th>
                                    <th>Symptom/Events</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dialysisRun.map((row, index) => {
                                    const isRowFromAPI = !!row.id;
                                    const rowValidation = validateInferenceRow(row, index);
                                    const isRowInvalid = !rowValidation.isValid && !rowValidation.isEmpty;

                                    return (
                                        <tr key={index} className={isRowFromAPI ? 'readonly-row' : ''}>
                                            <td className="catgory-cell">
                                                <TableInput
                                                    type="time-picker"
                                                    value={row.time}
                                                    onChange={(e) => handleTimeChangeForRow(index, e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    disabledHours={(hour) => hour > currentHour}
                                                    disabledMinutes={(minute) => minute > currentMinute}
                                                    error={isRowInvalid && !row.time}
                                                />
                                            </td>
                                            {['machine_blood_flow', 'dialysate_flow', 'tmp'].map(field => (
                                                <td key={field} className="input-cell">
                                                    <TableInput
                                                        type="number"
                                                        value={row[field]}
                                                        onChange={(e) => handleDialysisRunChange(index, field, e.target.value)}
                                                        readOnly={isRowFromAPI}
                                                        disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                        error={isRowInvalid && (row[field] === 0 || row[field] === '')}
                                                    />
                                                </td>
                                            ))}
                                            <td className="input-cell">
                                                <TableInput
                                                    type="text"
                                                    value={row.various_pressure}
                                                    onChange={(e) => handleDialysisRunChange(index, 'various_pressure', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && !row.various_pressure}
                                                />
                                            </td>
                                            <td className="input-cell">
                                                <TableInput
                                                    type="text"
                                                    value={row.heparin}
                                                    onChange={(e) => handleDialysisRunChange(index, 'heparin', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && !row.heparin}
                                                    maxLength={100}
                                                />
                                            </td>
                                            <td className="input-cell">
                                                <TableInput
                                                    type="number"
                                                    value={row.patient_pulse}
                                                    onChange={(e) => handleDialysisRunChange(index, 'patient_pulse', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && (row.patient_pulse === 0 || row.patient_pulse === '')}
                                                />
                                            </td>
                                            <td className="input-cell">
                                                <TableInput
                                                    type="text"
                                                    value={row.patient_bp}
                                                    onChange={(e) => handleDialysisRunChange(index, 'patient_bp', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && !row.patient_bp}
                                                    maxLength={20}
                                                />
                                            </td>
                                            <td className="input-cell">
                                                <TableInput
                                                    type="text"
                                                    value={row.symptoms_events}
                                                    onChange={(e) => handleDialysisRunChange(index, 'symptoms_events', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && !row.symptoms_events}
                                                />
                                            </td>
                                            <td className="input-cell">
                                                <TableInput
                                                    type="text"
                                                    value={row.drugs_fluids}
                                                    onChange={(e) => handleDialysisRunChange(index, 'drugs_fluids', e.target.value)}
                                                    readOnly={isRowFromAPI}
                                                    disabled={currentEditingRow !== null && currentEditingRow !== index && data.id}
                                                    error={isRowInvalid && !row.drugs_fluids}
                                                />
                                            </td>
                                            {data.id && (
                                                <td className="input-cell">
                                                    <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                                        {!row.id && rowValidation.isValid && !rowValidation.isEmpty && (
                                                            <button
                                                                className="delete-btn"
                                                                onClick={() => handleUpdateInference(index)}
                                                                style={{ backgroundColor: 'rgb(60, 104, 158)' }}
                                                                disabled={loading}
                                                            >
                                                                {loading ? 'Updating...' : 'Update'}
                                                            </button>
                                                        )}
                                                        {!row.id && hasRowData(row) && dialysisRun.length > 1 && (
                                                            <button
                                                                className="delete-btn"
                                                                onClick={() => removeDialysisRow(index)}
                                                                disabled={loading}
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                        {row.id && (
                                                            <span className="readonly-indicator" style={{ fontSize: '12px', color: '#666' }}>
                                                                Saved
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                            {!data.id && (
                                                <td className="input-cell">
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => removeDialysisRow(index)}
                                                        disabled={dialysisRun.length <= 1 || loading}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="table-actions">
                            <button type="button" className="add-btn" onClick={addDialysisRow}>
                                Add Row
                            </button>
                        </div>
                    </div>
                </FormSection>

                <div className="section">
                    {renderFieldGroup(footerFields1, "")}
                    {data.id && <label className='dialysis-by'><b>Dialysis started by: </b>{data.dialysis_started_by_name}</label>}
                </div>
            </div>
            <div className="general-form-actions">
                <button type="button" className="cancel-btn" disabled={loading} style={{ cursor: loading ? 'wait' : 'pointer' }} onClick={resetForm}>Cancel</button>
                {data.id ? (
                    <>
                        <button type="button" className="submit-btn" disabled={loading} style={{ cursor: loading ? 'wait' : 'pointer' }} onClick={handleEnd}>End</button>
                        <button type="button" className="submit-btn terminate-btn" disabled={loading} style={{ cursor: loading ? 'wait' : 'pointer' }} onClick={handleTerminate}>Terminate</button>
                    </>
                ) : (
                    <button type="submit" className="submit-btn" disabled={loading} style={{ cursor: loading ? 'wait' : 'pointer' }} onClick={handleSubmit}>Save</button>
                )}
            </div>
            <TerminateDialog
                isOpen={showTerminateDialog}
                onClose={() => setShowTerminateDialog(false)}
                onConfirm={handleTerminateConfirm}
            />
        </div>
    );
};

export default HemodialysisFlowSheet;